/*
 * SockItToMe Norge — lightweight wishlist (no dependencies, no jQuery).
 * Stores product handles in localStorage and renders a saved-items page client-side.
 * Custom elements: <sitm-wishlist-toggle>, <sitm-wishlist-count>, <sitm-wishlist-list>
 */
(function () {
  const KEY = 'sitm_wishlist';

  const read = () => {
    try { return JSON.parse(localStorage.getItem(KEY)) || []; }
    catch (e) { return []; }
  };
  const write = (arr) => {
    localStorage.setItem(KEY, JSON.stringify(arr));
    document.dispatchEvent(new CustomEvent('sitm:wishlist:change', { detail: arr }));
  };
  const has = (handle) => read().indexOf(handle) > -1;
  const toggle = (handle) => {
    const arr = read();
    const i = arr.indexOf(handle);
    if (i > -1) arr.splice(i, 1); else arr.push(handle);
    write(arr);
    return arr.indexOf(handle) > -1;
  };

  const money = (cents) => (cents / 100).toFixed(2).replace('.', ',') + ' kr';

  class WishlistToggle extends HTMLElement {
    connectedCallback() {
      this.handle = this.getAttribute('handle');
      this.button = this.querySelector('button');
      if (!this.button) return;
      this.reflect(has(this.handle));
      this.button.addEventListener('click', (e) => {
        e.preventDefault();
        this.reflect(toggle(this.handle));
      });
      this._onChange = () => this.reflect(has(this.handle));
      document.addEventListener('sitm:wishlist:change', this._onChange);
    }
    disconnectedCallback() {
      document.removeEventListener('sitm:wishlist:change', this._onChange);
    }
    reflect(active) {
      this.classList.toggle('is-active', active);
      this.button.setAttribute('aria-pressed', active ? 'true' : 'false');
      const label = active ? this.getAttribute('label-remove') : this.getAttribute('label-add');
      if (label) this.button.setAttribute('aria-label', label);
    }
  }

  class WishlistCount extends HTMLElement {
    connectedCallback() {
      this.update();
      this._onChange = () => this.update();
      document.addEventListener('sitm:wishlist:change', this._onChange);
    }
    disconnectedCallback() {
      document.removeEventListener('sitm:wishlist:change', this._onChange);
    }
    update() {
      const n = read().length;
      this.textContent = n;
      this.hidden = n === 0;
    }
  }

  class WishlistList extends HTMLElement {
    connectedCallback() {
      this.emptyText = this.getAttribute('empty-text') || 'Ønskelisten din er tom.';
      this.render();
      this._onChange = () => this.render();
      document.addEventListener('sitm:wishlist:change', this._onChange);
    }
    disconnectedCallback() {
      document.removeEventListener('sitm:wishlist:change', this._onChange);
    }
    async render() {
      const handles = read();
      if (!handles.length) {
        this.innerHTML = '<p class="sitm-wishlist-empty">' + this.emptyText + '</p>';
        return;
      }
      const results = await Promise.all(handles.map((h) =>
        fetch('/products/' + encodeURIComponent(h) + '.js')
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null)
      ));
      const cards = results.filter(Boolean).map((p) => {
        const img = p.featured_image
          ? '<img src="' + p.featured_image + '&width=400" alt="' + (p.title || '').replace(/"/g, '&quot;') + '" loading="lazy" width="400">'
          : '';
        return (
          '<div class="sitm-wishlist-card">' +
            '<a href="' + p.url + '" class="sitm-wishlist-card__link">' +
              '<div class="sitm-wishlist-card__media">' + img + '</div>' +
              '<p class="sitm-wishlist-card__title">' + p.title + '</p>' +
              '<p class="sitm-wishlist-card__price">' + money(p.price) + '</p>' +
            '</a>' +
            '<sitm-wishlist-toggle handle="' + p.handle + '" label-remove="Fjern fra ønskelisten">' +
              '<button type="button" class="sitm-wishlist-remove" aria-label="Fjern fra ønskelisten">Fjern</button>' +
            '</sitm-wishlist-toggle>' +
          '</div>'
        );
      });
      this.innerHTML = '<div class="sitm-wishlist-grid">' + cards.join('') + '</div>';
    }
  }

  if (!customElements.get('sitm-wishlist-toggle')) customElements.define('sitm-wishlist-toggle', WishlistToggle);
  if (!customElements.get('sitm-wishlist-count')) customElements.define('sitm-wishlist-count', WishlistCount);
  if (!customElements.get('sitm-wishlist-list')) customElements.define('sitm-wishlist-list', WishlistList);
})();
